import { zip, strToU8 } from 'fflate'
import type {
  BcfCatalogEntry,
  BcfComment,
  BcfExtensions,
  BcfTopic,
  ParsedBcf,
} from './bcfParser'
import type {
  Viewpoint,
  VpVec3,
  VpClippingPlane,
} from '@viewer/lib/viewpoint'
import {
  direccaoParaIfc,
  pontoParaIfc,
  type Referencial,
} from './bcfCoordenadas'
import { lerVisualizacao } from './bcfVisinfo'

/**
 * BCF 2.1 writer. Mirrors the parser: builds the inverse zip from the
 * in-memory `ParsedBcf` plus optional attached viewpoints. Output is
 * always BCF 2.1, broadest compatibility (BIMcollab, Solibri, ARCHICAD
 * et al), even when the source file was 3.0. We only down-rev the
 * envelope; the topic fields we carry are the intersection that 2.1
 * supports without loss.
 *
 * Snapshot PNGs are passed in as Blobs by the caller (read from
 * IndexedDB at export time). The writer doesn't touch the viewer or
 * IndexedDB itself, keeps it pure for testability.
 */

export interface AttachedViewpoint {
  /** GUID used for the bcfv/png filenames inside the topic folder. */
  guid: string
  /** BCF VisualizationInfo data. */
  viewpoint: Viewpoint
  /** Snapshot (PNG/JPG) bytes; null skips the snapshot file. */
  snapshot: Uint8Array | null
  /** Extension for the snapshot file ("png" by default). Picked from
   * the source Blob's MIME type when the snapshot came from an image
   * upload. */
  snapshotExt?: 'png' | 'jpg' | 'jpeg' | 'webp' | 'gif'
  /** Referencial do modelo a que o viewpoint pertence (ver
   *  `bcfCoordenadas.ts`). Sem ele assume-se a identidade, que é o
   *  certo para um modelo sem georreferenciação. */
  referencial?: Referencial | null
}

const XML_DECL = '<?xml version="1.0" encoding="UTF-8"?>\n'

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function tag(name: string, value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return ''
  const text = typeof value === 'number' ? String(value) : escapeXml(value)
  return `<${name}>${text}</${name}>`
}

/** A versão do envelope que se escreve.
 *
 *  2.1 continua a ser a omissão, por ser a que toda a gente lê. A 3.0
 *  existe para quem a pede (BIMcollab e Solibri recentes, servidores
 *  BCF-API 3.0) e difere no envelope: labels num contentor, comentários
 *  e viewpoints dentro do tópico, comentários sem Status, câmaras com
 *  AspectRatio, e TopicType/TopicStatus obrigatórios. */
export type VersaoBcf = '2.1' | '3.0'

function buildVersion(versao: VersaoBcf): Uint8Array {
  const xml =
    versao === '3.0'
      ? XML_DECL + '<Version VersionId="3.0"/>'
      : XML_DECL
        + '<Version VersionId="2.1">'
        + '<DetailedVersion>2.1</DetailedVersion>'
        + '</Version>'
  return strToU8(xml)
}

function buildComment(c: BcfComment): string {
  return (
    `<Comment Guid="${escapeXml(c.guid)}">`
    + tag('Date', c.date)
    + tag('Author', c.author)
    + tag('Comment', c.comment)
    + tag('ModifiedDate', c.modifiedDate)
    + tag('ModifiedAuthor', c.modifiedAuthor)
    // O Comment da 2.1 e da 3.0 não tem Status (era da 2.0): o esquema
    // é Date, Author, Comment, Viewpoint, ModifiedDate, ModifiedAuthor.
    + '</Comment>'
  )
}

function snapshotFilename(vp: AttachedViewpoint): string {
  const ext = vp.snapshotExt ?? 'png'
  return `${vp.guid}.${ext}`
}

function buildViewpointRef(vp: AttachedViewpoint, index: number, versao: VersaoBcf): string {
  // Na 3.0 cada referência chama-se ViewPoint, dentro de um contentor
  // Viewpoints; na 2.1 cada uma é um Viewpoints solto.
  const nome = versao === '3.0' ? 'ViewPoint' : 'Viewpoints'
  return (
    `<${nome} Guid="${escapeXml(vp.guid)}">`
    + `<Viewpoint>${escapeXml(vp.guid)}.bcfv</Viewpoint>`
    + (vp.snapshot
      ? `<Snapshot>${escapeXml(snapshotFilename(vp))}</Snapshot>`
      : '')
    + tag('Index', index)
    + `</${nome}>`
  )
}

/** Na 3.0 o TopicType e o TopicStatus são obrigatórios. Um tópico sem
 *  eles leva o primeiro valor do catálogo do ficheiro, que é o que um
 *  utilizador escolheria, e "Unknown" se o catálogo também estiver
 *  vazio. Fica dito aqui para não parecer que o valor veio do autor. */
function valorObrigatorio(actual: string | null, catalogo: BcfCatalogEntry[]): string {
  if (actual) return actual
  const primeiro = [...catalogo].sort((a, b) => a.sortOrder - b.sortOrder).find((e) => e.label.length > 0)
  return primeiro?.label ?? 'Unknown'
}

function buildMarkup(
  topic: BcfTopic,
  viewpoints: AttachedViewpoint[],
  versao: VersaoBcf,
  ext: BcfExtensions,
): Uint8Array {
  // BCF 2.1 emits each label as its own `<Labels>` element (plural,
  // text content). BCF 3.0 uses one `<Labels>` container with a
  // `<Label>` per value. Our parser reads both shapes for resilience.
  const labelsBlock =
    versao === '3.0'
      ? topic.labels.length === 0
        ? ''
        : '<Labels>' + topic.labels.map((l) => `<Label>${escapeXml(l)}</Label>`).join('') + '</Labels>'
      : topic.labels.map((l) => `<Labels>${escapeXml(l)}</Labels>`).join('')

  const commentsBlock = topic.comments.map((c) => buildComment(c)).join('')
  const viewpointsBlock = viewpoints
    .map((vp, i) => buildViewpointRef(vp, i + 1, versao))
    .join('')

  const atributos =
    versao === '3.0'
      ? ` TopicType="${escapeXml(valorObrigatorio(topic.type, ext.types))}"`
        + ` TopicStatus="${escapeXml(valorObrigatorio(topic.status, ext.statuses))}"`
      : (topic.type ? ` TopicType="${escapeXml(topic.type)}"` : '')
        + (topic.status ? ` TopicStatus="${escapeXml(topic.status)}"` : '')

  // A ordem dos campos é a do esquema: AssignedTo antes do Stage nas
  // duas versões. A 3.0 mete os comentários e os viewpoints dentro do
  // tópico; a 2.1 põe-nos ao lado.
  const camposComuns =
    tag('Title', topic.title)
    + tag('Priority', topic.priority)
    + tag('Index', topic.index)
    + labelsBlock
    + tag('CreationDate', topic.creationDate)
    + tag('CreationAuthor', topic.creationAuthor)
    + tag('ModifiedDate', topic.modifiedDate)
    + tag('ModifiedAuthor', topic.modifiedAuthor)
    + tag('DueDate', topic.dueDate)
  // Os tópicos relacionados vêm depois da Description (e do BimSnippet e
  // das DocumentReferences, que não escrevemos), nas duas versões: na
  // 2.1 cada um é um RelatedTopic solto, na 3.0 vão num contentor
  // RelatedTopics, antes dos comentários.
  const relacionados = [...new Set(topic.relatedTopics ?? [])].filter(
    (g) => g.length > 0 && g !== topic.guid,
  )
  const relacionadosBlock = relacionados
    .map((g) => `<RelatedTopic Guid="${escapeXml(g)}"/>`)
    .join('')
  const topicBlock =
    versao === '3.0'
      ? `<Topic Guid="${escapeXml(topic.guid)}"${atributos}>`
        + camposComuns
        + tag('AssignedTo', topic.assignedTo)
        + tag('Stage', topic.stage)
        + tag('Description', topic.description)
        + (relacionadosBlock ? '<RelatedTopics>' + relacionadosBlock + '</RelatedTopics>' : '')
        + (commentsBlock ? '<Comments>' + commentsBlock + '</Comments>' : '')
        + (viewpointsBlock ? '<Viewpoints>' + viewpointsBlock + '</Viewpoints>' : '')
        + '</Topic>'
      : `<Topic Guid="${escapeXml(topic.guid)}"${atributos}>`
        + camposComuns
        + tag('AssignedTo', topic.assignedTo)
        + tag('Stage', topic.stage)
        + tag('Description', topic.description)
        + relacionadosBlock
        + '</Topic>'
        + commentsBlock
        + viewpointsBlock

  const xml =
    XML_DECL
    + '<Markup xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"'
    + ' xmlns:xsd="http://www.w3.org/2001/XMLSchema">'
    + topicBlock
    + '</Markup>'
  return strToU8(xml)
}

function xyz(v: VpVec3): string {
  return `<X>${v.x}</X><Y>${v.y}</Y><Z>${v.z}</Z>`
}

function v3Tag(name: string, v: VpVec3): string {
  return `<${name}>${xyz(v)}</${name}>`
}

function planeTag(p: VpClippingPlane, ref: Referencial | null | undefined): string {
  return (
    '<ClippingPlane>'
    + v3Tag('Location', pontoParaIfc(p.location, ref))
    + v3Tag('Direction', direccaoParaIfc(p.direction, ref))
    + '</ClippingPlane>'
  )
}

/** A câmara em coordenadas IFC. O viewpoint interno está em
 *  coordenadas da cena (Y para cima); o BCF quer as do ficheiro IFC
 *  (Z para cima). A conversão é só aqui. */
function cameraBlock(vp: Viewpoint, versao: VersaoBcf, ref: Referencial | null | undefined): string {
  const cam = vp.camera
  // Na 3.0 o AspectRatio é obrigatório nas duas câmaras. Guardamos o da
  // captura; um viewpoint antigo sem ele leva 1, e fica dito.
  const aspecto = versao === '3.0' ? `<AspectRatio>${cam.aspect_ratio ?? 1}</AspectRatio>` : ''
  const comum =
    v3Tag('CameraViewPoint', pontoParaIfc(cam.position, ref))
    + v3Tag('CameraDirection', direccaoParaIfc(cam.direction, ref))
    + v3Tag('CameraUpVector', direccaoParaIfc(cam.up, ref))
  if (cam.mode === 'perspective') {
    return (
      '<PerspectiveCamera>'
      + comum
      + `<FieldOfView>${cam.field_of_view ?? 60}</FieldOfView>`
      + aspecto
      + '</PerspectiveCamera>'
    )
  }
  return (
    '<OrthogonalCamera>'
    + comum
    + `<ViewToWorldScale>${cam.view_to_world_scale ?? 1}</ViewToWorldScale>`
    + aspecto
    + '</OrthogonalCamera>'
  )
}

function componentsList(ids: readonly string[]): string {
  return ids.map((g) => `<Component IfcGuid="${escapeXml(g)}"/>`).join('')
}

/** Components pela ordem do esquema (2.1 e 3.0): Selection antes de
 *  Visibility. As excepções dependem da visibilidade por omissão:
 *  com DefaultVisibility a true são os escondidos, a false são os
 *  visíveis. */
function componentsBlock(vp: Viewpoint, versao: VersaoBcf): string {
  const c = vp.components
  const excepcoes = c.default_visibility ? c.hidden_global_ids : c.visible_global_ids
  const selection =
    c.selected_global_ids.length === 0
      ? ''
      : '<Selection>' + componentsList(c.selected_global_ids) + '</Selection>'
  const visibility =
    `<Visibility DefaultVisibility="${c.default_visibility ? 'true' : 'false'}">`
    + (excepcoes.length > 0 ? '<Exceptions>' + componentsList(excepcoes) + '</Exceptions>' : '')
    + '</Visibility>'
  return '<Components>' + selection + visibility + coloringBlock(vp, versao) + '</Components>'
}

/** Coloring, depois da Visibility (ordem do esquema nas duas versões).
 *  Cor em RRGGBB; na 3.0 os componentes de cada cor vão dentro de um
 *  contentor Components, na 2.1 vão soltos. */
function coloringBlock(vp: Viewpoint, versao: VersaoBcf): string {
  const cores = (vp.components.coloring ?? []).filter(
    (c) => /^[0-9A-Fa-f]{6}([0-9A-Fa-f]{2})?$/.test(c.color) && c.global_ids.length > 0,
  )
  if (cores.length === 0) return ''
  return (
    '<Coloring>'
    + cores
      .map((c) => {
        const lista = componentsList(c.global_ids)
        return (
          `<Color Color="${c.color.toUpperCase()}">`
          + (versao === '3.0' ? '<Components>' + lista + '</Components>' : lista)
          + '</Color>'
        )
      })
      .join('')
    + '</Coloring>'
  )
}

/** Um importado reescrito noutra versão: a câmara e os cortes vêm do
 *  `.bcfv` original lido com a identidade e escrevem-se com a identidade,
 *  por isso saem nas coordenadas exactas do ficheiro, sejam quais forem
 *  os modelos abertos. A câmara gravada na importação não serve: foi lida
 *  num referencial que pode ter sido o errado (antes da regra comum, o do
 *  modelo activo). Sem câmara legível no original, fica a gravada. */
function doOriginal(av: AttachedViewpoint): AttachedViewpoint {
  const xml = av.viewpoint.bcfv_original?.xml
  const lida = xml ? lerVisualizacao(xml, null) : null
  if (!lida?.camera) return av
  return {
    ...av,
    viewpoint: { ...av.viewpoint, camera: lida.camera, clipping_planes: lida.clipping_planes },
    referencial: null,
  }
}

function buildBcfv(entrada: AttachedViewpoint, versao: VersaoBcf): Uint8Array {
  // Importado e nunca recapturado: sai tal como entrou, para não se
  // perder nada de quem o criou. Só se reescreve se a versão do ficheiro
  // de destino for outra, porque o envelope da 3.0 não é o da 2.1.
  if (entrada.viewpoint.bcfv_original && entrada.viewpoint.bcfv_original.versao === versao) {
    return strToU8(entrada.viewpoint.bcfv_original.xml)
  }
  const av = doOriginal(entrada)
  const vp = av.viewpoint
  const planes =
    vp.clipping_planes.length === 0
      ? ''
      : '<ClippingPlanes>'
        + vp.clipping_planes.map((p) => planeTag(p, av.referencial)).join('')
        + '</ClippingPlanes>'

  const xml =
    XML_DECL
    + '<VisualizationInfo'
    + ' xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"'
    + ' xmlns:xsd="http://www.w3.org/2001/XMLSchema"'
    // O Guid é o do viewpoint no markup: é por ele que as ferramentas
    // ligam o .bcfv à referência e aos comentários.
    + ` Guid="${escapeXml(av.guid)}"`
    + '>'
    + componentsBlock(vp, versao)
    + cameraBlock(vp, versao, av.referencial)
    + planes
    + '</VisualizationInfo>'
  return strToU8(xml)
}

/** Build the standard `extensions.xml` consumed by every BCF tool.
 * Carries only the labels (no colour, no sort) since the spec doesn't
 * model that: VN2R-specific metadata lands in `vn2r-extensions.json`
 * (see buildExtensionsSidecar). */
function buildExtensionsXml(ext: BcfExtensions, utilizadores: readonly string[] = []): Uint8Array {
  const sortAndExtract = (entries: BcfCatalogEntry[]): string[] =>
    [...entries]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((e) => e.label)
      .filter((l) => l.length > 0)

  const block = (container: string, child: string, values: string[]): string => {
    if (values.length === 0) return ''
    return (
      `<${container}>`
      + values.map((v) => `<${child}>${escapeXml(v)}</${child}>`).join('')
      + `</${container}>`
    )
  }

  const xml =
    XML_DECL
    + '<Extensions'
    + ' xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"'
    + ' xmlns:xsd="http://www.w3.org/2001/XMLSchema">'
    + block('TopicTypes', 'TopicType', sortAndExtract(ext.types))
    + block('TopicStatuses', 'TopicStatus', sortAndExtract(ext.statuses))
    + block('Priorities', 'Priority', sortAndExtract(ext.priorities))
    + block('TopicLabels', 'TopicLabel', sortAndExtract(ext.labels))
    // Users entre TopicLabels e SnippetTypes/Stages, a ordem do esquema.
    // São os valores que o AssignedTo pode tomar noutras ferramentas.
    + block('Users', 'User', [...new Set(utilizadores.map((u) => u.trim()).filter((u) => u.length > 0))])
    + block('Stages', 'Stage', sortAndExtract(ext.stages))
    + '</Extensions>'
  return strToU8(xml)
}

/** Sidecar JSON with VN2R-specific metadata that the BCF spec doesn't
 * model: per-entry colour, sort order, and the isTerminal flag for
 * statuses. Other BCF tools that don't preserve unknown files lose
 * this on round-trip but the catalog values themselves survive via
 * the standard `extensions.xml`. */
function buildExtensionsSidecar(ext: BcfExtensions): Uint8Array {
  return strToU8(JSON.stringify(ext, null, 2))
}

/** Sidecar JSON with watchers per topic guid. BCF spec has no
 * watchers concept, so this is purely VN2R metadata. Same round-trip
 * caveat as `vn2r-extensions.json`. */
function buildTopicsSidecar(topics: BcfTopic[]): Uint8Array {
  const payload = {
    version: 1,
    topics: topics
      .filter((t) => t.watchers.length > 0)
      .map((t) => ({ guid: t.guid, watchers: t.watchers })),
  }
  return strToU8(JSON.stringify(payload, null, 2))
}

/** O `project.bcfp`, com o identificador de projecto do BCF. É o que
 *  faz o mesmo BCF ser reconhecido como tal por quem o importar: na
 *  sincronização, cada BCF do workspace vai pelo seu identificador, e um
 *  ficheiro sem ele só se encontra no browser que o criou. Ordem do
 *  esquema: 2.1 ProjectExtension(Project, ExtensionSchema), 3.0
 *  ProjectInfo(Project); Project(Name) com o atributo ProjectId. */
function buildProject(id: string, nome: string | null, versao: VersaoBcf): Uint8Array {
  const projecto =
    `<Project ProjectId="${escapeXml(id)}">`
    + (nome ? tag('Name', nome) : '')
    + '</Project>'
  const xml =
    versao === '3.0'
      ? XML_DECL + '<ProjectInfo>' + projecto + '</ProjectInfo>'
      : XML_DECL + '<ProjectExtension>' + projecto + '<ExtensionSchema></ExtensionSchema></ProjectExtension>'
  return strToU8(xml)
}

async function zipAsync(files: Record<string, Uint8Array>): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    zip(files, { level: 6 }, (err, data) => {
      if (err) reject(err)
      else resolve(data)
    })
  })
}

export async function buildBcfZip(
  parsed: ParsedBcf,
  attachments: Record<string, AttachedViewpoint[]> = {},
  opcoes: {
    versao?: VersaoBcf
    /** Valores para a lista Users do extensions.xml (quem pode ficar
     *  com um tópico). Sem eles a lista não se escreve. */
    utilizadores?: readonly string[]
  } = {},
): Promise<Blob> {
  const versao = opcoes.versao ?? '2.1'
  const files: Record<string, Uint8Array> = {
    'bcf.version': buildVersion(versao),
    'extensions.xml': buildExtensionsXml(parsed.extensions, opcoes.utilizadores),
    'vn2r-extensions.json': buildExtensionsSidecar(parsed.extensions),
  }
  if (parsed.sourceProjectId) {
    files['project.bcfp'] = buildProject(parsed.sourceProjectId, parsed.sourceProjectName, versao)
  }

  // Watchers sidecar only when at least one topic has watchers, so a
  // BCF without watchers stays as compact as before.
  const topicsSidecar = buildTopicsSidecar(parsed.topics)
  // Empty payload writes a JSON with `topics: []`; skip it instead.
  if (parsed.topics.some((t) => t.watchers.length > 0)) {
    files['vn2r-topics.json'] = topicsSidecar
  }

  for (const topic of parsed.topics) {
    const folder = topic.guid
    const vps = attachments[topic.guid] ?? []
    files[`${folder}/markup.bcf`] = buildMarkup(topic, vps, versao, parsed.extensions)
    for (const vp of vps) {
      files[`${folder}/${vp.guid}.bcfv`] = buildBcfv(vp, versao)
      if (vp.snapshot) {
        files[`${folder}/${snapshotFilename(vp)}`] = vp.snapshot
      }
    }
  }

  const zipped = await zipAsync(files)
  // Slice to a fresh ArrayBuffer to satisfy the Blob signature; fflate
  // returns a Uint8Array whose buffer type depends on environment.
  const buf = zipped.slice().buffer
  return new Blob([buf], { type: 'application/zip' })
}
